import { Injectable } from '@angular/core';
import { BehaviorSubject, defer, Observable, Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { ControlNumbersService, FreeRequestStatus } from './control-numbers.service';

/**
 * Último estado conocido de la solicitud gratuita de números de control (ADM-827), compartido
 * entre «Mejora tu Fina», la alerta del POS y el checkout mix. Es singleton: `AuthService.logout()`
 * lo reinicia para que no se vea el saldo de otro comercio en la misma pestaña.
 */
@Injectable({
    providedIn: 'root',
})
export class ControlNumbersStatusService {
    private readonly statusSubject = new BehaviorSubject<FreeRequestStatus | null>(null);
    readonly status$: Observable<FreeRequestStatus | null> = this.statusSubject.asObservable();

    /** Sube con cada `reset()`: una respuesta pedida antes del reinicio no se publica. */
    private generation = 0;
    private refreshing = false;
    private refreshSub: Subscription | null = null;

    constructor(private controlNumbersService: ControlNumbersService) {}

    get status(): FreeRequestStatus | null {
        return this.statusSubject.value;
    }

    /** Consulta el GET y publica la respuesta. El error llega a quien se suscribe; el estado queda como estaba. */
    load$(): Observable<FreeRequestStatus | null> {
        return defer(() => {
            const generation = this.generation;
            return this.controlNumbersService.getFreeRequestStatus$().pipe(
                tap(status => {
                    if (generation === this.generation) {
                        this.statusSubject.next(status);
                    }
                }),
            );
        });
    }

    /** Recarga en segundo plano: un solo GET en vuelo y sin toast si falla. */
    refresh(): void {
        if (this.refreshing) {
            return;
        }
        this.refreshing = true;
        this.refreshSub = this.load$()
            .pipe(
                finalize(() => {
                    this.refreshing = false;
                    this.refreshSub = null;
                }),
            )
            .subscribe({ error: () => undefined });
    }

    /**
     * Tras emitir una factura fiscal. Solo consulta si el último estado estaba sobre el umbral:
     * en o bajo el umbral la alerta ya está puesta y no se repite el GET en cada emisión. Sin
     * estado conocido (comercio sin la compuerta fiscal o GET fallido) no consulta.
     */
    refreshAfterFiscalInvoice(): void {
        const last = this.statusSubject.value;
        if (!last || !(Number(last.available) > Number(last.threshold))) {
            return;
        }
        this.refresh();
    }

    reset(): void {
        this.generation += 1;
        this.refreshSub?.unsubscribe();
        this.refreshSub = null;
        this.refreshing = false;
        this.statusSubject.next(null);
    }
}
